import { useEffect, useState } from 'react';
import { makeStyles, Drawer, List, ListItem, ListItemText, ListItemIcon, Divider } from '@material-ui/core';
import { NavLink } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import HomeIcon from '@material-ui/icons/Home';
import DescriptionIcon from '@material-ui/icons/Description';
import { Typography } from '@material-ui/core'; // Fix import

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: '#ffffff', // Match content background
    color: '#333',
    display: 'flex',
    flexDirection: 'column',
    borderTopRightRadius: '16px', // Rounded top right corner
    borderBottomRightRadius: '16px', // Rounded bottom right corner
    margin: theme.spacing(1), // Add some margin so it floats a bit
    height: `calc(100% - ${theme.spacing(2)}px)`, // Adjust height for margin
    padding: theme.spacing(1), // Add internal padding
  },
  drawerContainer: {
    overflow: 'auto',
  },
  link: {
    textDecoration: 'none',
    color: '#555',
    borderRadius: '8px', // Rounded corners for list items
    marginBottom: 4,     // Slight spacing between items
    '&:hover': {
      color: '#000',
      backgroundColor: 'rgba(0,0,0,0.08)',
    },
    '&.active': {
      color: '#000',
      backgroundColor: 'rgba(0,0,0,0.16)', // Keeping background
      fontWeight: 'bold', // Making text bold
      '& .MuiListItemIcon-root': { // Making icon black
          color: '#000'
      }
    },
  },
  listItemText: {
     margin: 0, // Reset margin to reduce space
     '& span': {
        fontSize: '0.9rem',
     }
  },
  listItemIcon: {
      minWidth: 32, // Reduce space between icon and text
  },
  sectionHeader: {
      padding: theme.spacing(2, 2, 1, 2),
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#999',
      fontWeight: 'bold',
      marginLeft: theme.spacing(1), // Align with list item text which has padding
  }
}));

type SidebarProps = {
  open: boolean;
  onClose: () => void;
};

export const Sidebar = ({ open, onClose }: SidebarProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEntities = async () => {
        try {
            setLoading(true);
            const response = await catalogApi.getEntities({
                filter: {
                    kind: 'Component',
                    'spec.type': 'documentation',
                },
            });
            const sorted = response.items.sort((a, b) => (
                (a.metadata.title || a.metadata.name).localeCompare(b.metadata.title || b.metadata.name)
            ));
            setEntities(sorted);
        } catch (e) {
            console.error('Failed to fetch entities for sidebar', e);
        } finally {
            setLoading(false);
        }
    };
    fetchEntities();
  }, [catalogApi, identityApi]);

  return (
    <Drawer
      className={classes.drawer}
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      <div className={classes.drawerContainer}>
        <List style={{ paddingTop: 0 }}>
            <ListItem button component={NavLink} to="/docs" className={classes.link} end onClick={onClose}>
            <ListItemIcon className={classes.listItemIcon}><HomeIcon style={{ color: 'inherit' }} /></ListItemIcon>
            <ListItemText primary="Home" className={classes.listItemText} />
            </ListItem>
        </List>

        <Divider style={{ backgroundColor: 'rgba(0,0,0,0.12)' }} />

        <Typography className={classes.sectionHeader}>
            Your Documentation
        </Typography>

        <List>
            {loading ? (
                <ListItem><ListItemText primary="Loading..." className={classes.listItemText}/></ListItem>
            ) : (
                entities.map((entity) => {
                    const linkPath = `/docs/${entity.metadata.namespace || 'default'}/${entity.kind}/${entity.metadata.name}`;
                    return (
                        <ListItem
                            button
                            key={entity.metadata.uid}
                            component={NavLink}
                            to={linkPath}
                            className={classes.link}
                            onClick={onClose}
                        >
                            <ListItemIcon className={classes.listItemIcon}><DescriptionIcon style={{ fontSize: 18, color: 'inherit' }}/></ListItemIcon>
                            <ListItemText
                                primary={entity.metadata.title || entity.metadata.name}
                                secondary={entity.metadata.description?.substring(0, 30) + (entity.metadata.description && entity.metadata.description.length > 30 ? '...' : '')}
                                secondaryTypographyProps={{ style: { color: '#888', fontSize: '0.75rem' } }}
                                className={classes.listItemText}
                            />
                        </ListItem>
                    );
                })
            )}
        </List>
      </div>
    </Drawer>
  );
};
